import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { centralApi } from '@marque/api/central'
import type { ModulePackageSummary } from '@marque/api/central'
import { ModuleChangeFeed } from '../components/ModuleChangeFeed'
import { ModuleLifecyclePanel } from '../components/ModuleLifecyclePanel'

// AB#2123 — the ops-portal home for the definition change feed. AB#2148 — the left pane groups the
// installed packages by module (one row per module, expandable version history) instead of one row
// per package version; the change feed keys on the module's LATEST package id, the same id the
// module-detail / upgrade-impact endpoints take.

interface ModuleGroup {
    moduleId: string
    displayName: string
    deprecated: boolean
    latest: ModulePackageSummary
    versions: ModulePackageSummary[]
}

// Group package rows (already moduleId-asc, version-desc from the API) into one entry per module.
// Exported as a test seam.
// eslint-disable-next-line react-refresh/only-export-components
export function groupPackages(rows: ModulePackageSummary[]): ModuleGroup[] {
    const groups = new Map<string, ModuleGroup>()
    for (const row of rows) {
        const existing = groups.get(row.moduleId)
        if (!existing) {
            groups.set(row.moduleId, {
                moduleId: row.moduleId,
                displayName: row.displayName || row.moduleId,
                // AB#2081 sets the deprecated flag on every package row of a module, so any row carries it.
                deprecated: row.deprecated,
                latest: row,
                versions: [row],
            })
        } else {
            existing.versions.push(row)
            if (row.version > existing.latest.version) { existing.latest = row }
            existing.deprecated = existing.deprecated || row.deprecated
        }
    }
    return [...groups.values()]
}

function installedDate(iso: string) {
    return new Date(iso).toLocaleDateString()
}

export function ModulesPage() {
    const { data: modules = [], isLoading, isError } = useQuery({
        queryKey: ['module-packages'],
        queryFn: () => centralApi.listModulePackages(),
    })
    const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null)
    const [expandedModuleId, setExpandedModuleId] = useState<string | null>(null)

    const groups = groupPackages(modules)
    const selected = groups.find(g => g.moduleId === selectedModuleId) ?? null

    return (
        <div className="py-2">
            <h1 className="text-lg font-semibold text-neutral-900 mb-4">Modules</h1>

            {isLoading && <p className="text-sm text-neutral-500">Loading installed modules…</p>}
            {isError && <p className="text-sm text-red-600">Could not load installed modules.</p>}

            {!isLoading && !isError && groups.length === 0 && (
                <p className="text-sm text-neutral-500">No modules are installed.</p>
            )}

            {groups.length > 0 && (
                <div className="flex flex-col lg:flex-row gap-6 items-start">
                    <ul className="w-full lg:w-80 shrink-0 border border-neutral-200 rounded divide-y divide-neutral-100">
                        {groups.map(g => {
                            const active = g.moduleId === selectedModuleId
                            const expanded = g.moduleId === expandedModuleId
                            return (
                                <li key={g.moduleId}>
                                    <div
                                        className={`flex items-center ${active ? 'bg-neutral-100' : 'hover:bg-neutral-50'}`}
                                    >
                                        <button
                                            type="button"
                                            data-testid={`module-row-${g.moduleId}`}
                                            onClick={() => setSelectedModuleId(g.moduleId)}
                                            className={`flex-1 min-w-0 text-left px-3 py-2 text-sm ${active ? 'font-medium' : ''}`}
                                        >
                                            <span className="text-neutral-900">{g.displayName}</span>
                                            <span className="ml-1.5 text-[11px] text-neutral-400">v{g.latest.semver}</span>
                                            {g.deprecated && (
                                                <span className="ml-1.5 text-[10px] text-amber-700">deprecated</span>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`module-versions-toggle-${g.moduleId}`}
                                            onClick={() => setExpandedModuleId(expanded ? null : g.moduleId)}
                                            title={expanded ? 'Hide versions' : `Show ${g.versions.length} installed version(s)`}
                                            className="px-2 py-2 text-[11px] text-neutral-400 hover:text-neutral-700 shrink-0"
                                        >
                                            {g.versions.length} ver {expanded ? '▾' : '▸'}
                                        </button>
                                    </div>
                                    {expanded && (
                                        <ul data-testid={`module-versions-${g.moduleId}`} className="bg-neutral-50 border-t border-neutral-100">
                                            {g.versions.map(v => (
                                                <li key={v.id} className="px-3 py-1 pl-6 text-[11px] text-neutral-500 flex justify-between">
                                                    <span className="font-mono">v{v.semver}</span>
                                                    <span>{installedDate(v.installedAt)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            )
                        })}
                    </ul>

                    <div className="w-full flex-1 min-w-0">
                        {selected
                            ? (
                                <>
                                    {/* AB#2149 — deprecate / uninstall / revoke-all lifecycle actions */}
                                    <ModuleLifecyclePanel
                                        moduleId={selected.moduleId}
                                        packageId={selected.latest.id}
                                        deprecated={selected.deprecated}
                                        onUninstalled={() => setSelectedModuleId(null)}
                                    />
                                    <ModuleChangeFeed moduleId={selected.latest.id} />
                                </>
                            )
                            : <p className="text-sm text-neutral-500">Select a module to see its change history.</p>}
                    </div>
                </div>
            )}
        </div>
    )
}
